import { useEffect, useState } from "react";
import api from "../lib/api";
import { PlantBadge, plantAlt, Section, Spinner } from "../components/Bits";
import PageHero from "../components/PageHero";

const isBaerAron = (a, b) => {
  const n = [a?.name_de, b?.name_de].map((x) => (x || "").toLowerCase());
  return n.some((x) => x.includes("bärlauch")) && n.some((x) => x.includes("aronstab"));
};

export default function Compare() {
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [left, setLeft] = useState(null);
  const [right, setRight] = useState(null);

  useEffect(() => {
    api.get("/plants").then((r) => {
      setPlants(r.data || []);
      setLoading(false);
    });
  }, []);

  if (loading) return <Section><Spinner /></Section>;

  const PlantPicker = ({ value, onChange, label, testId }) => (
    <select value={value || ""} onChange={(e) => onChange(plants.find((p) => p.id === e.target.value))} className="w-full px-4 py-3 rounded-xl border bg-white" style={{ borderColor: "var(--wp-border)" }} data-testid={testId}>
      <option value="">{label}</option>
      {plants.map((p) => <option key={p.id} value={p.id}>{p.name_de} ({p.category})</option>)}
    </select>
  );

  const PlantCol = ({ plant }) => {
    if (!plant) return <div className="plant-card p-10 text-center opacity-50">Pflanze wählen</div>;
    return (
      <div className="plant-card overflow-hidden">
        <img src={plant.image_url} alt={plantAlt(plant)} loading="lazy" className="aspect-[4/3] w-full object-cover" />
        <div className="p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl">{plant.name_de}</h3>
            <PlantBadge category={plant.category} />
          </div>
          <div className="italic text-sm opacity-70">{plant.name_lat}</div>
          <div><div className="overline mb-1">Beschreibung</div><p className="text-sm">{plant.short_description}</p></div>
          {plant.ingredients && <div><div className="overline mb-1">Inhaltsstoffe</div><p className="text-sm">{plant.ingredients}</p></div>}
          {plant.look_alikes && <div><div className="overline mb-1">Verwechslungen</div><p className="text-sm">{plant.look_alikes}</p></div>}
          {plant.warnings && <div className="text-sm p-3 rounded-lg" style={{ backgroundColor: "rgba(167,58,58,0.08)" }}><b>Hinweis:</b> {plant.warnings}</div>}
        </div>
      </div>
    );
  };

  return (
    <Section>
      <PageHero slug="compare" variant="soft"
        defaultOverline="Vergleichs-Modus"
        defaultTitle="Zwei Pflanzen nebeneinander"
        defaultSubtitle="Ideal zur Sicherheit – Bärlauch vs. Maiglöckchen, Giersch vs. Hundspetersilie."
      />
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <PlantPicker value={left?.id} onChange={setLeft} label="← Links wählen" testId="compare-left-select" />
        <PlantPicker value={right?.id} onChange={setRight} label="Rechts wählen →" testId="compare-right-select" />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <PlantCol plant={left} />
        <PlantCol plant={right} />
      </div>
      {isBaerAron(left, right) && (
        <div className="mt-8 plant-card p-6" data-testid="compare-baerlauch-aronstab">
          <div className="overline mb-1">Im Feld nebeneinander</div>
          <h3 className="font-display text-2xl mb-4">Bärlauch und Gefleckter Aronstab</h3>
          <div className="grid grid-cols-2 gap-4">
            <figure>
              <img src="/fotos/baerlauch-aronstab.webp" alt="Bärlauch und Gefleckter Aronstab wachsen nebeneinander" loading="lazy" className="w-full aspect-[3/4] object-cover rounded-xl" />
              <figcaption className="text-xs opacity-70 mt-2">Bärlauch und Gefleckter Aronstab wachsen nebeneinander</figcaption>
            </figure>
            <figure>
              <img src="/fotos/baerlauch-aronstab-hand.webp" alt="Bärlauch-Blatt in der Hand, daneben ein Aronstab-Blatt" loading="lazy" className="w-full aspect-[3/4] object-cover rounded-xl" />
              <figcaption className="text-xs opacity-70 mt-2">Bärlauch-Blatt in der Hand, daneben ein Aronstab-Blatt</figcaption>
            </figure>
          </div>
        </div>
      )}
    </Section>
  );
}
